import { z } from 'zod'

// Member role options
export const MemberRoles = ['OWNER', 'ADMIN', 'MEMBER', 'GUEST'] as const

export type MemberRole = (typeof MemberRoles)[number]

// Roles an invitation may carry. Never OWNER: an invite (or its acceptance)
// can't make someone the workspace owner.
export const InvitableMemberRoles = ['ADMIN', 'MEMBER', 'GUEST'] as const

export type InvitableMemberRole = (typeof InvitableMemberRoles)[number]

// Organization invite schema with validation
export const OrganizationInviteSchema = z.object({
  id: z.string().uuid().optional(),
  organizationId: z.string().uuid(),
  email: z.string().email('Invalid email address'),
  name: z.string().nullable().optional(),
  role: z.enum(MemberRoles).default('MEMBER'),
  token: z.string().optional(),
  status: z.string().default('PENDING'),
  invitedBy: z.string().uuid(),
  permissionSetId: z.string().uuid().nullable().optional(),
  metadata: z.any().nullable().optional(), // For storing projectIds and other invite details
  expiresAt: z.date(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),

  // Relations (optional)
  organization: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
      logo: z.string().nullable().optional(),
      slug: z.string().optional(),
    })
    .optional(),

  inviter: z
    .object({
      id: z.string().uuid(),
      firstName: z.string().nullable().optional(),
      lastName: z.string().nullable().optional(),
      displayName: z.string().nullable().optional(),
      email: z.string().email(),
    })
    .optional(),
})

// Organization invite type
export type OrganizationInvite = z.infer<typeof OrganizationInviteSchema>

// DTO for creating an organization invite
export const CreateOrganizationInviteSchema = z.object({
  email: z.string().email('Invalid email address'),
  name: z.string().nullable().optional(),
  role: z.enum(InvitableMemberRoles).default('MEMBER'),
  permissionSetId: z.string().uuid().nullable().optional(),
  metadata: z.any().optional(), // For storing projectIds and other invite details
})

export type CreateOrganizationInviteDTO = z.infer<
  typeof CreateOrganizationInviteSchema
>

// DTO for bulk creating organization invites
export const BulkCreateOrganizationInvitesSchema = z.object({
  invites: z.array(CreateOrganizationInviteSchema),
})

export type BulkCreateOrganizationInvitesDTO = z.infer<
  typeof BulkCreateOrganizationInvitesSchema
>

// DTO for accepting an invite
export const AcceptOrganizationInviteSchema = z.object({
  token: z.string(),
})

export type AcceptOrganizationInviteDTO = z.infer<
  typeof AcceptOrganizationInviteSchema
>

// Invite status options
export const InviteStatuses = [
  'PENDING',
  'ACCEPTED',
  'EXPIRED',
  'CANCELLED',
] as const

export type InviteStatus = (typeof InviteStatuses)[number]

// Response type for bulk invites
export interface OrganizationInviteResponse {
  success: boolean
  invites: Array<{
    email: string
    status: 'SENT' | 'FAILED'
    message?: string
  }>
}
